import * as React from 'react'
import { ChevronDown, MessageSquare, X } from 'lucide-react'
import {
  useListProblemTasksQuery,
  useSetProblemTasksCompleteMutation,
  type ProblemOccurrence,
  type ProblemTask,
  type WorkScope
} from '@axilon-apps/domains-ontology-ui-state'
import { TaskBulkAction, TaskCompletionCircle } from '../primitives/TaskCompletion'
import { TaskPriority } from '../primitives/TaskPriority'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible'
import { Button } from '../ui/button'
import { cn } from '../../lib/utils'
import { useChatHandoff } from '../../lib/use-chat-handoff'
import {
  occurrenceContext,
  problemTaskStatus,
  visibleProblemTasks,
  workMutationError,
  workPriority,
  type WorkStatus
} from './work-model'

export function WorkError({ message, retry }: { message: string; retry?: () => void }) {
  return (
    <p role="alert" className="m-0 px-3 py-3 text-13 text-st-down">
      {message}
      {retry && (
        <>
          {' '}
          <button
            type="button"
            onClick={retry}
            className="rounded text-brand underline focus-visible:ring-2 focus-visible:ring-brand"
          >
            Retry
          </button>
        </>
      )}
    </p>
  )
}

export function OccurrenceTasks({
  scope,
  occurrence,
  status = 'open',
  collapsible = false,
  details = false,
  readOnly = false,
  retained = false,
  selectedId,
  onSelect,
  onChanged
}: {
  scope: WorkScope
  occurrence: ProblemOccurrence
  status?: WorkStatus
  collapsible?: boolean
  details?: boolean
  readOnly?: boolean
  retained?: boolean
  selectedId?: string | null
  onSelect?: (id: string | null) => void
  onChanged?: (id: string) => void
}) {
  const query = useListProblemTasksQuery(
    { ...scope, id: occurrence.id },
    { refetchOnMountOrArgChange: true }
  )
  const [open, setOpen] = React.useState(false)
  const [mutate, mutation] = useSetProblemTasksCompleteMutation()
  const data = query.isError ? undefined : query.currentData
  const tasks = visibleProblemTasks(data?.tasks ?? [], status, retained)
  const [localSelected, setLocalSelected] = React.useState<string | null>(null)
  const selection = onSelect ? selectedId : localSelected
  const select = onSelect ?? setLocalSelected
  const setComplete = async (task?: ProblemTask) => {
    if (!data || mutation.isLoading) return
    try {
      await mutate({
        ...scope,
        id: occurrence.id,
        taskId: task?.id,
        action: (task ? task.work_status === 'done' : data.counts.all_done)
          ? 'restore'
          : 'complete',
        expectedVersion: task?.version ?? data.task_set_version,
        operationKey: crypto.randomUUID()
      }).unwrap()
      onChanged?.(occurrence.id)
    } catch {
      /* Mutation errors are rendered beside the controls. */
    }
  }
  const content = (
    <>
      {query.isError ? (
        <WorkError message="Couldn't load tasks." retry={() => void query.refetch()} />
      ) : !data ? (
        <p role="status" className="m-0 px-3 py-3 text-13 text-ink-3">
          Loading tasks…
        </p>
      ) : (
        <>
          {!readOnly &&
            occurrence.allowed_actions.edit &&
            status !== 'rejected' &&
            data.counts.open + data.counts.done > 0 && (
              <TaskBulkAction
                problemName={occurrence.problem_name}
                allDone={data.counts.all_done}
                saving={mutation.isLoading || query.isFetching}
                onClick={() => void setComplete()}
              />
            )}
          {tasks.length === 0 ? (
            <p className="m-0 px-3 py-3 text-13 text-ink-3">No tasks yet.</p>
          ) : (
            <ol className="m-0 flex list-none flex-col divide-y divide-line/60 p-0">
              {tasks.map((task) => (
                <OccurrenceTaskRow
                  key={task.id}
                  task={task}
                  occurrence={occurrence}
                  details={details}
                  selected={selection === task.id}
                  onSelect={() => select(selection === task.id ? null : task.id)}
                  saving={mutation.isLoading || query.isFetching || readOnly || !task.can_edit}
                  onComplete={() => void setComplete(task)}
                />
              ))}
            </ol>
          )}
        </>
      )}
      {mutation.error && (
        <WorkError message={workMutationError(mutation.error)} retry={() => void query.refetch()} />
      )}
    </>
  )
  if (!collapsible) return <div className="min-w-0">{content}</div>
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <section
        aria-label="Associated tasks"
        className="min-w-0 overflow-hidden rounded-lg border border-line/80 bg-card"
      >
        <h3 className="m-0">
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex w-full items-center gap-2 rounded-md bg-bg-sunk/25 px-3 py-2 text-left text-13 outline-none transition-colors hover:bg-accent/40 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand"
            >
              <span className="font-medium text-ink">Tasks</span>
              {data && (
                <span className="text-12 font-normal tabular-nums text-ink-3">
                  {data.counts.open} open · {data.counts.done} completed
                </span>
              )}
              <ChevronDown
                size={14}
                aria-hidden
                className={cn(
                  'ml-auto text-ink-3 transition-transform motion-reduce:transition-none',
                  open && 'rotate-180'
                )}
              />
            </button>
          </CollapsibleTrigger>
        </h3>
        <CollapsibleContent>
          <div className="border-t border-line/70">{content}</div>
        </CollapsibleContent>
      </section>
    </Collapsible>
  )
}

function OccurrenceTaskRow({
  task,
  occurrence,
  details,
  selected,
  onSelect,
  saving,
  onComplete
}: {
  task: ProblemTask
  occurrence: ProblemOccurrence
  details: boolean
  selected: boolean
  onSelect: () => void
  saving: boolean
  onComplete: () => void
}) {
  const { askInChat } = useChatHandoff()
  const rejected = problemTaskStatus(task) === 'rejected'
  const rowRef = React.useRef<HTMLLIElement>(null)
  React.useEffect(() => {
    if (selected) rowRef.current?.scrollIntoView?.({ block: 'nearest' })
  }, [selected])
  const label = (
    <>
      <span aria-hidden className="text-right text-12 tabular-nums text-ink-3">
        {task.sequence}.
      </span>
      <span
        className={cn(
          'min-w-0 break-words text-14 leading-5 font-medium',
          task.work_status === 'done' || rejected ? 'text-ink-3' : 'text-ink'
        )}
      >
        {task.title}
      </span>
      <span className="justify-self-end pr-1.5">
        <TaskPriority priority={workPriority(task.priority)} emptyLabel="No priority" />
      </span>
      {details && (
        <ChevronDown
          size={14}
          aria-hidden
          className={cn(
            'text-ink-3 transition-transform motion-reduce:transition-none',
            selected && 'rotate-180'
          )}
        />
      )}
    </>
  )
  return (
    <li ref={rowRef} value={task.sequence}>
      <div className="grid grid-cols-[1.25rem_1rem_minmax(0,1fr)_4rem_1rem] items-center gap-x-1.5 gap-y-1 px-3 py-2.5 transition-colors hover:bg-bg-sunk/45">
        {rejected ? (
          <X size={15} aria-label="Rejected" className="text-st-down" />
        ) : (
          <TaskCompletionCircle
            title={task.title}
            done={task.work_status === 'done'}
            saving={saving}
            onClick={onComplete}
          />
        )}
        {details ? (
          <button
            type="button"
            aria-expanded={selected}
            aria-label={task.title}
            onClick={onSelect}
            className="col-span-4 grid grid-cols-subgrid items-center rounded text-left outline-none focus-visible:ring-2 focus-visible:ring-brand"
          >
            {label}
          </button>
        ) : (
          label
        )}
      </div>
      {details && selected && (
        <div className="min-w-0 px-3 pb-3 pt-1">
          {task.instructions && (
            <p className="m-0 mb-2.5 whitespace-pre-wrap break-words rounded-md border border-line bg-bg-elev px-3 py-2.5 text-13 leading-relaxed text-ink-2">
              {task.instructions}
            </p>
          )}
          <div className="flex items-center justify-between gap-3">
            <Button
              size="xs"
              variant="outline"
              onClick={() =>
                askInChat({
                  title: task.title,
                  context: {
                    ...occurrenceContext(occurrence),
                    taskId: task.id,
                    runId: task.producing_run_id
                  }
                })
              }
              className="h-7 text-brand"
            >
              <MessageSquare size={14} />
              Ask in chat
            </Button>
          </div>
        </div>
      )}
    </li>
  )
}
